// @ts-ignore - redux-form doesn't have official TypeScript types
import React, { ReactNode } from 'react';

import Autocomplete from '@/components/autocomplete/components/Autocomplete';
import DropdownWrapper from '@/components/dropdown/components/DropdownWrapper';
import FormInput from '@/components/input/FormInput';
import Textarea from '@/components/input/Textarea';
import RadioButton from '@/components/radioButton/RadioButton';
import Switch from '@/components/Switch';

interface RenderFormInputProps {
	input: any;
	meta: any;
	autoComplete?: string;
	color?: string;
	disabled?: boolean;
	disabledError?: boolean;
	externalError?: string | null;
	floatingLabel?: boolean;
	focusOnMount?: boolean;
	label?: string;
	loading?: boolean;
	mask?: string | (string | RegExp)[];
	noBlur?: boolean;
	onDelete?: () => void;
	showError?: boolean;
	type?: string;
}

export const renderFormInput = ({
	autoComplete,
	color,
	disabled,
	disabledError,
	externalError,
	floatingLabel,
	focusOnMount,
	input: { onChange, value, onBlur, name },
	label,
	loading,
	mask,
	meta: { error, dirty, touched },
	noBlur = true,
	onDelete,
	showError,
	type,
}: RenderFormInputProps) => {
	return (
		<div className="form-group">
			<FormInput
				{...({
					floatingLabel,
					autoComplete,
					disabledError,
					error:
						externalError ||
						(dirty && (!noBlur ? touched : true) && error ? error : '') ||
						(showError && error),
					actionNode: externalError ? (
						<span className="icon icon-incorrect-red" />
					) : undefined,
					label,
					onBlur: !noBlur ? onBlur : () => {},
					onChange,
					onDelete,
					type,
					value,
					color,
					name,
					focusOnMount,
					loading,
					disabled,
					mask,
				} as any)}
			/>
		</div>
	);
};

interface RenderFormTextareaProps {
	input: any;
	meta: any;
	disabled?: boolean;
	dataName?: string;
	focus?: boolean;
	classname?: string;
	color?: string;
	floatingLabel?: boolean;
	label?: string;
	loading?: boolean;
	noBlur?: boolean;
	showError?: boolean;
	rows?: number;
}

export const renderFormTextarea = ({
	disabled,
	input: { onChange, value, onBlur, name, onFocus },
	meta: { error, dirty, touched },
	dataName,
	focus,
	classname,
	color,
	floatingLabel,
	label,
	loading,
	noBlur = true,
	showError,
	rows,
}: RenderFormTextareaProps) => {
	return (
		<div className="form-group">
			<Textarea
				{...({
					name,
					dataName,
					focus,
					onFocus,
					onChange,
					onBlur: !noBlur ? onBlur : () => {},
					value,
					disabled,
					classname,
					floatingLabel,
					error:
						(dirty && (!noBlur ? touched : true) && error ? error : '') ||
						(showError && error),
					label,
					color,
					loading,
					rows: rows ? String(rows) : undefined,
				} as any)}
			/>
		</div>
	);
};

interface RenderFormDropdownProps {
	input: any;
	meta?: any;
	name: string;
	options: any[];
	placeholder?: string;
	optionValueKey?: string;
	optionTitleKey?: string;
	optionIconKey?: string;
	optionFlagKey?: string;
	optionAvaKey?: string;
	optionAvaPosition?: boolean;
	optionSectionKey?: string;
	optionSectionBodyKey?: string;
	nested?: boolean;
	floatingLabel?: boolean;
	menuHeight?: string;
	menuPosition?: string;
	color?: string;
	handleDd?: () => void;
	drawerTitle?: string;
	type?: string;
	withSearch?: boolean;
	searchPlaceholder?: string;
	classname?: string;
	disabled?: boolean;
	searchFocusOnMount?: boolean;
	fallbackOption?: any;
}

export const renderFormDropdown = ({
	input: { onChange, value },
	name,
	options,
	placeholder,
	optionValueKey = 'id',
	optionTitleKey = 'title',
	optionIconKey = 'icon',
	optionFlagKey = 'code',
	optionAvaKey = 'logo',
	optionAvaPosition = false,
	optionSectionKey = 'section',
	optionSectionBodyKey = 'body',
	nested = false,
	floatingLabel = true,
	menuHeight = '400px',
	menuPosition = 'left',
	color = 'gray',
	handleDd,
	drawerTitle,
	type = 'form',
	withSearch = false,
	searchPlaceholder = '',
	classname,
	disabled,
	searchFocusOnMount,
	fallbackOption,
}: RenderFormDropdownProps) => {
	return (
		<div className="form-field">
			<DropdownWrapper
				{...({
					name,
					optionValueKey,
					optionTitleKey,
					optionIconKey,
					optionAvaKey,
					optionAvaPosition,
					optionSectionKey,
					optionSectionBodyKey,
					optionFlagKey,
					nested,
					options,
					type,
					menuHeight,
					onChange,
					value,
					color,
					placeholder,
					menuPosition,
					floatingLabel,
					handleDd,
					drawerTitle,
					withSearch,
					searchPlaceholder,
					className: classname,
					disabled,
					searchFocusOnMount,
					fallbackOption,
				} as any)}
			/>
			<span className="error-msg" />
		</div>
	);
};

interface RenderAutocompleteProps {
	input: any;
	meta?: any;
	name: string;
	options: any[];
	placeholder?: string;
	optionValueKey?: string;
	optionTitleKey?: string;
	nested?: boolean;
	floatingLabel?: boolean;
	menuHeight?: string;
	loading?: boolean;
	itemSelect?: (item: any) => void;
	color?: string;
	menuPosition?: string;
	optionIconKey?: string;
	optionSectionKey?: string;
	optionSectionBodyKey?: string;
	optionFlagKey?: string;
	optionSubTitleKey?: string;
}

export const renderAutocomplete = ({
	input: { onChange, value },
	name,
	options,
	placeholder,
	optionValueKey = 'id',
	optionTitleKey = 'title',
	nested = false,
	floatingLabel = true,
	menuHeight = '300px',
	loading,
	itemSelect,
	color = 'gray',
	menuPosition = 'left',
	optionIconKey = 'icon',
	optionSectionKey = 'section',
	optionSectionBodyKey = 'body',
	optionFlagKey = 'code',
	optionSubTitleKey = 'city',
}: RenderAutocompleteProps) => {
	return (
		<div className="form-field">
			<Autocomplete
				{...({
					name,
					optionValueKey,
					optionTitleKey,
					optionIconKey,
					optionSectionKey,
					optionSectionBodyKey,
					nested,
					optionFlagKey,
					options,
					type: 'form',
					menuHeight,
					onChange,
					value,
					color,
					placeholder,
					menuPosition,
					floatingLabel,
					loading,
					itemSelect,
					optionSubTitleKey,
				} as any)}
			/>
			<span className="error-msg" />
		</div>
	);
};

interface RenderSwitchProps {
	input: any;
	meta?: any;
	title?: string;
}

export const renderSwitch = ({ input: { onChange, value }, title }: RenderSwitchProps) => {
	return <Switch checked={value} title={title} onChange={onChange} />;
};

interface RenderRadioButtonProps {
	input: any;
	meta?: any;
	type?: 'checkbox' | 'radio';
	text?: ReactNode;
	disabled?: boolean;
}

export const renderRadioButton = ({
	input: { onChange, value },
	type,
	text,
	disabled,
}: RenderRadioButtonProps) => {
	return (
		<RadioButton
			className="form-checkbox"
			disabled={disabled}
			type={type}
			text={text}
			value={value}
			checked={Boolean(+value)}
			onChange={onChange}
			showBevel={false}
		/>
	);
};
